import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import api from "@/lib/api";
import VisitRemindersWidget from "@/components/ehr/VisitRemindersWidget";
import {
  Calendar, Clock, User, Users, AlertTriangle, AlertCircle,
  CheckCircle, XCircle, TrendingUp, TrendingDown, Activity,
  Loader2, RefreshCw, ChevronRight, Eye, Bell, Heart,
  Stethoscope, UserCircle, ClipboardList, BarChart3
} from "lucide-react";

const DISCIPLINES = [
  { value: "RN", label: "Registered Nurse", color: "bg-blue-100 text-blue-700", icon: Stethoscope },
  { value: "LPN", label: "Licensed Practical Nurse", color: "bg-blue-100 text-blue-700", icon: Stethoscope },
  { value: "SW", label: "Social Worker", color: "bg-pink-100 text-pink-700", icon: Users },
  { value: "Chaplain", label: "Chaplain", color: "bg-purple-100 text-purple-700", icon: Heart },
  { value: "MD", label: "Physician", color: "bg-violet-100 text-violet-700", icon: UserCircle },
  { value: "NP", label: "Nurse Practitioner", color: "bg-violet-100 text-violet-700", icon: UserCircle },
  { value: "Aide", label: "Home Health Aide", color: "bg-orange-100 text-orange-700", icon: User },
  { value: "PT", label: "Physical Therapy", color: "bg-teal-100 text-teal-700", icon: Activity },
  { value: "OT", label: "Occupational Therapy", color: "bg-teal-100 text-teal-700", icon: Activity },
  { value: "ST", label: "Speech Therapy", color: "bg-teal-100 text-teal-700", icon: Activity },
  { value: "RD", label: "Registered Dietitian", color: "bg-green-100 text-green-700", icon: ClipboardList }
];

const SEVERITY_COLORS = {
  critical: "bg-red-100 text-red-700 border-red-300",
  high: "bg-orange-100 text-orange-700 border-orange-300",
  medium: "bg-amber-100 text-amber-700 border-amber-300",
  low: "bg-blue-100 text-blue-700 border-blue-300"
};

export default function VisitFrequencyPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  
  // Dashboard Data
  const [dashboardData, setDashboardData] = useState(null);
  const [missedVisits, setMissedVisits] = useState([]);
  
  // Patient Compliance
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [complianceReport, setComplianceReport] = useState(null);
  const [loadingCompliance, setLoadingCompliance] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardRes, missedRes] = await Promise.all([
        api.get("/ehr/visit-frequency/dashboard"),
        api.get("/ehr/visit-frequency/missed-visits")
      ]);
      setDashboardData(dashboardRes.data.summary);
      setMissedVisits(missedRes.data.missed_visits || []);
    } catch (error) {
      console.error("Error loading dashboard:", error);
      toast.error("Failed to load visit data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const loadPatientCompliance = async (patientId) => {
    setLoadingCompliance(true);
    try {
      const res = await api.get(`/ehr/visit-frequency/compliance/${patientId}`);
      setComplianceReport(res.data);
      setSelectedPatient(patientId);
    } catch (error) {
      toast.error("Failed to load compliance report");
    } finally {
      setLoadingCompliance(false);
    }
  };

  const acknowledgeMissedVisit = async (visitId) => {
    try {
      await api.post(`/ehr/visit-frequency/missed-visits/${visitId}/acknowledge`, {
        reason: "Reviewed and addressed"
      });
      toast.success("Missed visit acknowledged");
      loadDashboard();
    } catch (error) {
      toast.error("Failed to acknowledge");
    }
  };

  const getDisciplineInfo = (discipline) => {
    return DISCIPLINES.find(d => d.value === discipline) || DISCIPLINES[0];
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical":
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case "high":
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      case "medium":
        return <Clock className="w-5 h-5 text-amber-600" />;
      default:
        return <Bell className="w-5 h-5 text-blue-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="visit-frequency-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="w-8 h-8" />
                <h1 className="text-3xl font-bold">Visit Frequency Tracker</h1>
              </div>
              <p className="text-teal-100">
                Monitor visit compliance and track missed visits across all disciplines
              </p>
            </div>
            <Button variant="secondary" onClick={loadDashboard} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-l-4 border-l-green-500" data-testid="stat-compliant">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Compliant Patients</p>
                  <p className="text-3xl font-bold text-green-600">
                    {dashboardData?.compliant_patients || 0}
                  </p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500" data-testid="stat-at-risk">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">At Risk</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {dashboardData?.at_risk_patients || 0}
                  </p>
                </div>
                <AlertCircle className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500" data-testid="stat-critical">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Critical</p>
                  <p className="text-3xl font-bold text-red-600">
                    {dashboardData?.critical_patients || 0}
                  </p>
                </div>
                <AlertTriangle className="w-10 h-10 text-red-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500" data-testid="stat-total">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Patients</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {dashboardData?.total_patients || 0}
                  </p>
                </div>
                <Users className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="tab-dashboard">
              <BarChart3 className="w-4 h-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="missed" className="flex items-center gap-2" data-testid="tab-missed">
              <AlertTriangle className="w-4 h-4" />
              Missed Visits ({missedVisits.length})
            </TabsTrigger>
            <TabsTrigger value="reminders" className="flex items-center gap-2" data-testid="tab-reminders">
              <Bell className="w-4 h-4" />
              Reminders
            </TabsTrigger>
          </TabsList>

          {/* DASHBOARD TAB */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Discipline Breakdown */}
            {dashboardData?.by_discipline && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Compliance by Discipline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Object.entries(dashboardData.by_discipline).map(([discipline, data]) => {
                      const disciplineInfo = getDisciplineInfo(discipline);
                      const total = (data.compliant || 0) + (data.at_risk || 0);
                      const complianceRate = total > 0 ? ((data.compliant / total) * 100) : 100;
                      
                      return (
                        <div key={discipline} className="p-4 border rounded-lg" data-testid={`discipline-${discipline}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={disciplineInfo.color}>{discipline}</Badge>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Compliant</span>
                              <span className="font-medium text-green-600">{data.compliant || 0}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">At Risk</span>
                              <span className="font-medium text-amber-600">{data.at_risk || 0}</span>
                            </div>
                            <Progress value={complianceRate} className="h-2" />
                            <p className="text-xs text-center text-muted-foreground">
                              {complianceRate.toFixed(0)}% compliant
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Missed */}
            {dashboardData?.recent_missed?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="w-5 h-5" />
                    Recent Missed Visits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData.recent_missed.map((missed, idx) => {
                      const disciplineInfo = getDisciplineInfo(missed.discipline);
                      return (
                        <div
                          key={idx}
                          className={`p-4 border rounded-lg ${SEVERITY_COLORS[missed.severity] || SEVERITY_COLORS.medium}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getSeverityIcon(missed.severity)}
                              <div>
                                <p className="font-medium">{missed.patient_name}</p>
                                <p className="text-sm opacity-80">
                                  {disciplineInfo.label} visit overdue
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline">{missed.severity}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* MISSED VISITS TAB */}
          <TabsContent value="missed" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Missed Visit Tracker
                </CardTitle>
                <CardDescription>
                  Patients below required visit frequency for their discipline orders
                </CardDescription>
              </CardHeader>
              <CardContent>
                {missedVisits.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <p className="text-lg font-medium">All visits on track!</p>
                    <p className="text-muted-foreground">No missed visits detected</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Group by severity */}
                    {["critical", "high", "medium", "low"].map((severity) => {
                      const severityVisits = missedVisits.filter(v => v.severity === severity);
                      if (severityVisits.length === 0) return null;
                      
                      return (
                        <div key={severity}>
                          <h3 className="text-sm font-medium uppercase text-muted-foreground mb-3 flex items-center gap-2">
                            {getSeverityIcon(severity)}
                            {severity} ({severityVisits.length})
                          </h3>
                          <div className="space-y-3">
                            {severityVisits.map((visit) => {
                              const disciplineInfo = getDisciplineInfo(visit.discipline);
                              const DisciplineIcon = disciplineInfo.icon;
                              
                              return (
                                <div
                                  key={visit.id}
                                  className={`p-4 border rounded-lg ${SEVERITY_COLORS[severity]}`}
                                  data-testid={`missed-visit-${visit.id}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${disciplineInfo.color}`}>
                                        <DisciplineIcon className="w-5 h-5" />
                                      </div>
                                      <div>
                                        <p className="font-medium">{visit.patient_name}</p>
                                        <div className="flex items-center gap-2 text-sm">
                                          <Badge className={disciplineInfo.color}>{visit.discipline}</Badge>
                                          <span className="text-muted-foreground">
                                            Expected: {visit.expected_date}
                                          </span>
                                        </div>
                                        {visit.reason && (
                                          <p className="text-sm mt-1 opacity-80">{visit.reason}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => loadPatientCompliance(visit.patient_id)}
                                        data-testid={`view-compliance-${visit.id}`}
                                      >
                                        <Eye className="w-4 h-4 mr-1" />
                                        View
                                      </Button>
                                      {!visit.acknowledged && (
                                        <Button
                                          size="sm"
                                          onClick={() => acknowledgeMissedVisit(visit.id)}
                                          data-testid={`acknowledge-${visit.id}`}
                                        >
                                          <CheckCircle className="w-4 h-4 mr-1" />
                                          Acknowledge
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* REMINDERS TAB */}
          <TabsContent value="reminders" className="space-y-6">
            <VisitRemindersWidget />
          </TabsContent>
        </Tabs>
      </div>

      {/* Compliance Report Dialog */}
      <Dialog open={!!complianceReport} onOpenChange={() => setComplianceReport(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Visit Compliance Report</DialogTitle>
            <DialogDescription>
              {complianceReport?.patient_name} • Last {complianceReport?.reporting_period_start?.split("T")[0]} to {complianceReport?.reporting_period_end?.split("T")[0]}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[500px] pr-4">
            {loadingCompliance ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Overall Compliance */}
                <div className="text-center p-6 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Overall Compliance Rate</p>
                  <p className={`text-4xl font-bold ${
                    complianceReport?.overall_compliance_rate >= 80 ? "text-green-600" :
                    complianceReport?.overall_compliance_rate >= 50 ? "text-amber-600" : "text-red-600"
                  }`}>
                    {complianceReport?.overall_compliance_rate?.toFixed(1)}%
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {complianceReport?.total_visits_completed} of {complianceReport?.total_visits_required} visits completed
                  </p>
                </div>

                {/* Alerts */}
                {complianceReport?.alerts?.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      Alerts
                    </h3>
                    {complianceReport.alerts.map((alert, idx) => (
                      <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                        {alert}
                      </div>
                    ))}
                  </div>
                )}

                {/* By Discipline */}
                <div>
                  <h3 className="font-medium mb-3">By Discipline</h3>
                  <div className="space-y-3">
                    {complianceReport?.disciplines && Object.entries(complianceReport.disciplines).map(([discipline, data]) => {
                      const disciplineInfo = getDisciplineInfo(discipline);
                      return (
                        <div key={discipline} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge className={disciplineInfo.color}>{discipline}</Badge>
                              <span className="text-sm text-muted-foreground">{data.frequency}</span>
                            </div>
                            <span className={`font-medium ${
                              data.is_compliant ? "text-green-600" : "text-red-600"
                            }`}>
                              {data.compliance_rate?.toFixed(0)}%
                            </span>
                          </div>
                          <div className="flex justify-between text-sm text-muted-foreground mb-2">
                            <span>Required: {data.required}</span>
                            <span>Completed: {data.completed}</span>
                          </div>
                          <Progress 
                            value={data.compliance_rate || 0} 
                            className={`h-2 ${data.is_compliant ? "" : "[&>div]:bg-red-500"}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setComplianceReport(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
